import { inject, Injectable } from '@angular/core';

import { CreateEntityConfigService } from './config/create-entity-config.service';
import { CreateEntityWizardStore } from './create-entity-wizard.store';
import { FacilityFormValue } from './facility/facility-form.types';
import { JemmaFacilityApiService } from './facility/jemma-facility-api.service';
import { JemmaFacilityCreateRequest } from './facility/jemma-facility.types';
import { JemmaOrganizationApiService } from './organization/jemma-organization-api.service';
import { JemmaOrganizationCreateRequest } from './organization/jemma-organization.types';
import { OrganizationFormValue } from './organization/organization-form.types';
import { JemmaStudyApiService } from './study/jemma-study-api.service';
import { JemmaStudyCreateRequest } from './study/jemma-study.types';
import { StudyFormValue } from './study/study-form.types';

/**
 * Owns the create (submit) requests for every entity tab: maps a form's value to its Jemma request
 * body, calls the matching API service with the current widget config, and reports the outcome to
 * `CreateEntityWizardStore`. The `*FormComponent`s only validate and delegate here — they never
 * touch an API service. Provided per-`<wcg-create-entity>` instance, like the other widget services.
 */
@Injectable()
export class CreateEntityCommandService {
    private readonly _config = inject(CreateEntityConfigService);
    private readonly _wizard = inject(CreateEntityWizardStore);
    private readonly _organizationApi = inject(JemmaOrganizationApiService);
    private readonly _facilityApi = inject(JemmaFacilityApiService);
    private readonly _studyApi = inject(JemmaStudyApiService);

    createOrganization(value: OrganizationFormValue): void {
        const _body: JemmaOrganizationCreateRequest = {
            organization_name: value.name,
            primary_organization_type_key: value.primaryType,
            primary_address: {
                address_line_1: value.addressLine1,
                address_line_2: value.addressLine2 || undefined,
                city: value.city,
                state: value.state,
                postal_code: value.zipCode || undefined,
                country_key: value.country
            },
            secondary_organization_type_keys: value.additionalType.length ? value.additionalType : undefined,
            status: 'active'
        };

        this._wizard.startSubmitting();
        this._organizationApi.createOrganization(this._config.$config(), _body).subscribe({
            next: organization => this._wizard.reportCreated({ kind: 'organization', data: organization }),
            error: () => this._wizard.reportError('Something went wrong creating the organization. Please try again.')
        });
    }

    createFacility(value: FacilityFormValue): void {
        const _body: JemmaFacilityCreateRequest = {
            facility_name: value.name,
            primary_address: {
                address_line_1: value.addressLine1,
                city: value.city,
                state: value.state,
                postal_code: value.zipCode || undefined,
                country_key: value.country
            },
            status: 'active'
        };

        this._wizard.startSubmitting();
        this._facilityApi.createFacility(this._config.$config(), _body).subscribe({
            next: facility => this._wizard.reportCreated({ kind: 'facility', data: facility }),
            error: () => this._wizard.reportError('Something went wrong creating the facility. Please try again.')
        });
    }

    createStudy(value: StudyFormValue): void {
        const _body: JemmaStudyCreateRequest = {
            study_name: value.name,
            study_label: value.label || undefined,
            study_description: value.description || undefined,
            study_status_key: value.status
        };

        this._wizard.startSubmitting();
        this._studyApi.createStudy(this._config.$config(), _body).subscribe({
            next: study => this._wizard.reportCreated({ kind: 'study', data: study }),
            error: () => this._wizard.reportError('Something went wrong creating the study. Please try again.')
        });
    }
}
